import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import UsageStats from '@/pages/developer/components/UsageStats';
import ApiDocs from '@/pages/developer/components/ApiDocs';
import ApiTestPanel from '@/pages/developer/components/ApiTestPanel';
import { useApiKeys } from '@/hooks/useApiKeys';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import type { ApiKey } from '@/hooks/useApiKeys';

type Tab = 'overview' | 'keys' | 'usage' | 'docs' | 'tester';

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
  { id: 'keys', label: 'API Keys', icon: 'ri-key-2-line' },
  { id: 'usage', label: 'Usage Analytics', icon: 'ri-bar-chart-2-line' },
  { id: 'docs', label: 'API Docs', icon: 'ri-file-code-line' },
  { id: 'tester', label: 'API Tester', icon: 'ri-terminal-line' },
];

const planLabel: Record<string, string> = {
  '1_2_year': '1–2 Year',
  '3_5_year': '3–5 Year',
  'lifetime': 'Lifetime',
};

const planExpiry = (plan: string) => {
  const start = new Date('2026-03-30');
  if (plan === 'lifetime') return 'Never expires';
  if (plan === '1_2_year') {
    const exp = new Date(start);
    exp.setFullYear(exp.getFullYear() + 2);
    return `Expires ${exp.toLocaleDateString()}`;
  }
  if (plan === '3_5_year') {
    const exp = new Date(start);
    exp.setFullYear(exp.getFullYear() + 5);
    return `Expires ${exp.toLocaleDateString()}`;
  }
  return '';
};

export default function DeveloperPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const { keys, loading: keysLoading, createKey, revokeKey, rotateKey } = useApiKeys();
  const { subscriptions, loading: subsLoading } = useSubscriptions();
  const activeApis = subscriptions.filter(s => s.status === 'active');

  const [copied, setCopied] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);
  const [rotateConfirm, setRotateConfirm] = useState<string | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedSubId, setSelectedSubId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [newlyCreated, setNewlyCreated] = useState<ApiKey | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const copyKey = (id: string, val: string) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCreate = async () => {
    setGenerateError(null);
    if (!newKeyName.trim()) return;
    // Must have an active subscription selected
    if (activeApis.length > 0 && !selectedSubId) {
      setGenerateError('Please select an active subscription to link this key to.');
      return;
    }
    const linkedSub = activeApis.find(s => s.id === selectedSubId);
    const keyName = linkedSub ? `${linkedSub.product_name}` : newKeyName.trim();
    setCreating(true);
    const key = await createKey(keyName);
    setCreating(false);
    setCreateModal(false);
    setNewKeyName('');
    setSelectedSubId('');
    if (key) {
      setNewlyCreated(key);
      setRevealed(key.id);
    }
  };

  const handleRevoke = async (id: string) => {
    setActionLoading(id);
    await revokeKey(id);
    setActionLoading(null);
    setRevokeConfirm(null);
  };

  const handleRotate = async (id: string, productName: string) => {
    setActionLoading(id);
    const newKey = await rotateKey(id, productName);
    setActionLoading(null);
    setRotateConfirm(null);
    if (newKey) {
      setNewlyCreated(newKey);
      setRevealed(newKey.id);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-5">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors ${tab === t.id ? 'bg-sidebar text-white' : 'bg-white border border-surface-3 text-gray-500 hover:border-gray-300'}`}
            >
              <i className={t.icon} />{t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* ── Overview ── */}
          {tab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5">
              {subsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-5 animate-pulse">
                      <div className="h-1.5 bg-gray-100 rounded mb-4" />
                      <div className="h-4 bg-gray-100 rounded w-32 mb-2" />
                      <div className="h-3 bg-gray-100 rounded w-20 mb-4" />
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {Array.from({ length: 3 }).map((_, j) => <div key={j} className="h-12 bg-gray-100 rounded-lg" />)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : activeApis.length === 0 ? (
                <div className="bg-white rounded-xl p-16 text-center">
                  <i className="ri-key-2-line text-4xl text-gray-200 block mb-4" />
                  <h3 className="font-semibold text-gray-700 mb-2">No active subscriptions</h3>
                  <p className="text-sm text-gray-400 mb-4">Purchase an API from the marketplace to get started.</p>
                  <button onClick={() => setTab('keys')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-sidebar text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-key-2-line" />Manage API Keys
                  </button>
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeApis.map((api, i) => {
                  const pct = api.requests_limit > 0 ? Math.round((api.requests_used / api.requests_limit) * 100) : 0;
                  const planColors = ['from-emerald-500 to-emerald-600', 'from-amber-500 to-orange-500', 'from-rose-500 to-red-600', 'from-brand-500 to-brand-600'];
                  return (
                    <motion.div key={api.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-white rounded-xl overflow-hidden">
                      <div className={`h-1.5 w-full bg-gradient-to-r ${planColors[i % planColors.length]}`} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{api.product_name}</h3>
                            <span className="text-[11px] text-gray-400">{api.pricing_type.replace(/_/g, '-')} plan</span>
                          </div>
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${api.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>{api.status}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                          <div className="bg-surface-2 rounded-lg p-2.5">
                            <div className="text-sm font-bold text-gray-900">99.4%</div>
                            <div className="text-[10px] text-gray-400">Success</div>
                          </div>
                          <div className="bg-surface-2 rounded-lg p-2.5">
                            <div className="text-sm font-bold text-gray-900">138ms</div>
                            <div className="text-[10px] text-gray-400">Latency</div>
                          </div>
                          <div className="bg-surface-2 rounded-lg p-2.5">
                            <div className="text-sm font-bold text-gray-900">{(api.requests_used / 1000).toFixed(1)}k</div>
                            <div className="text-[10px] text-gray-400">Calls</div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex justify-between text-[11px] mb-1">
                            <span className="text-gray-500">Usage</span>
                            <span className="text-gray-400">{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-100 rounded-full">
                            <div className={`h-full rounded-full ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-brand-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-400">
                            <i className="ri-calendar-line mr-1" />
                            {planLabel[api.pricing_type] ?? api.pricing_type} · {planExpiry(api.pricing_type)}
                          </span>
                          <button onClick={() => setTab('tester')} className="text-brand-600 hover:text-brand-700 font-medium cursor-pointer whitespace-nowrap">
                            Test API <i className="ri-arrow-right-s-line" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 text-sm mb-3">Subscription Logic</h3>
                  <div className="space-y-3">
                    {[
                      { plan: '1–2 Year Plan', desc: 'Access extended by +2 years from purchase date', icon: 'ri-calendar-2-line', color: 'text-amber-500 bg-amber-50' },
                      { plan: '3–5 Year Plan', desc: 'Access extended by +5 years from purchase date', icon: 'ri-calendar-check-line', color: 'text-brand-500 bg-brand-50' },
                      { plan: 'Lifetime Plan', desc: 'Permanent access — never expires, no renewal needed', icon: 'ri-infinity-line', color: 'text-green-600 bg-green-50' },
                    ].map(s => (
                      <div key={s.plan} className="flex items-start gap-3 p-3 bg-surface-2 rounded-lg">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${s.color}`}><i className={`${s.icon} text-base`} /></span>
                        <div>
                          <div className="text-xs font-semibold text-gray-800">{s.plan}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 text-sm mb-3">Quick Start</h3>
                  <div className="space-y-2">
                    {[
                      { step: '1', text: 'Generate an API key from the Keys tab', done: keys.some(k => k.status === 'active') || activeApis.length > 0 },
                      { step: '2', text: 'Add Authorization header to requests', done: false },
                      { step: '3', text: 'View endpoints in API Docs tab', done: false },
                      { step: '4', text: 'Test live requests in API Tester', done: false },
                    ].map(s => (
                      <div key={s.step} className="flex items-center gap-3 py-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${s.done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {s.done ? <i className="ri-check-line" /> : s.step}
                        </div>
                        <span className={`text-xs ${s.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{s.text}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setTab('tester')} className="mt-4 w-full py-2.5 bg-sidebar hover:opacity-90 text-white rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-opacity flex items-center justify-center gap-2">
                    <i className="ri-terminal-line" />Open API Tester
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── API Keys ── */}
          {tab === 'keys' && (
            <motion.div key="keys" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-5 py-3 flex items-start gap-3 flex-1 mr-4">
                  <i className="ri-shield-keyhole-line text-amber-500 mt-0.5 text-lg shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-amber-700">API Key Security</div>
                    <div className="text-xs text-amber-600 mt-0.5">Keep your keys secret. Never commit them to version control. Keys are stored securely in database.</div>
                  </div>
                </div>
                <button
                  onClick={() => setCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-sidebar hover:opacity-90 text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-opacity shrink-0"
                >
                  <i className="ri-add-line" />Generate Key
                </button>
              </div>

              {/* New key reveal banner */}
              <AnimatePresence>
                {newlyCreated && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3"
                  >
                    <i className="ri-checkbox-circle-fill text-green-500 text-lg mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-green-700 mb-1">Key generated — save it now!</div>
                      <div className="text-[11px] text-green-600 mb-2">This is the only time you&apos;ll see the full key. Copy and store it safely.</div>
                      <div className="bg-gray-900 rounded-lg px-3 py-2 flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono text-emerald-400 truncate">{newlyCreated.key_value}</code>
                        <button
                          onClick={() => copyKey('new', newlyCreated.key_value)}
                          className="text-gray-400 hover:text-white cursor-pointer shrink-0 transition-colors"
                        >
                          <i className={copied === 'new' ? 'ri-check-line text-green-400' : 'ri-file-copy-line'} />
                        </button>
                      </div>
                    </div>
                    <button onClick={() => setNewlyCreated(null)} className="text-green-400 hover:text-green-600 cursor-pointer shrink-0">
                      <i className="ri-close-line text-lg" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {keysLoading ? (
                <div className="flex items-center justify-center py-16">
                  <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
                </div>
              ) : keys.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-key-2-line text-gray-400 text-2xl" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">No API keys yet</h3>
                  <p className="text-sm text-gray-400 mb-4">Generate your first key to start making API calls.</p>
                  <button
                    onClick={() => setCreateModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-sidebar text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" />Generate First Key
                  </button>
                </div>
              ) : (
                keys.map((k, i) => (
                  <motion.div
                    key={k.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`bg-white rounded-xl p-5 ${k.status === 'revoked' ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm text-gray-900">{k.product_name}</span>
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${k.status === 'revoked' ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                            {k.status === 'revoked' ? 'Revoked' : 'Active'}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Created {new Date(k.created_at).toLocaleDateString()} · Last used {new Date(k.last_used_at).toLocaleDateString()} · {k.total_requests.toLocaleString()} requests
                        </div>
                      </div>
                      {actionLoading === k.id && (
                        <i className="ri-loader-4-line animate-spin text-gray-400 shrink-0" />
                      )}
                    </div>

                    <div className="bg-gray-900 rounded-lg px-4 py-3 flex items-center gap-3 mb-4">
                      <i className="ri-key-2-line text-brand-400 shrink-0" />
                      <code className="flex-1 text-xs font-mono text-emerald-400 min-w-0 truncate">
                        {revealed === k.id ? k.key_value : k.key_prefix}
                      </code>
                      {k.status !== 'revoked' && (
                        <button
                          onClick={() => setRevealed(revealed === k.id ? null : k.id)}
                          className="text-gray-400 hover:text-white cursor-pointer shrink-0 transition-colors"
                        >
                          <i className={revealed === k.id ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </button>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => copyKey(k.id, revealed === k.id ? k.key_value : k.key_prefix)}
                        disabled={k.status === 'revoked'}
                        className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-surface-3 text-gray-600 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <i className={copied === k.id ? 'ri-check-line text-green-500' : 'ri-file-copy-line'} />
                        {copied === k.id ? 'Copied!' : 'Copy'}
                      </button>
                      {k.status !== 'revoked' && (
                        <>
                          <button
                            onClick={() => setRotateConfirm(k.id)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-amber-100 text-amber-600 hover:bg-amber-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"
                          >
                            <i className="ri-refresh-line" />Rotate
                          </button>
                          <button
                            onClick={() => setRevokeConfirm(k.id)}
                            disabled={!!actionLoading}
                            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"
                          >
                            <i className="ri-forbid-line" />Revoke
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          )}

          {tab === 'usage' && (
            <motion.div key="usage" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <UsageStats />
            </motion.div>
          )}

          {tab === 'docs' && (
            <motion.div key="docs" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ApiDocs />
            </motion.div>
          )}

          {tab === 'tester' && (
            <motion.div key="tester" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ApiTestPanel />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Create Key Modal */}
      <AnimatePresence>
        {createModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-sm p-6">
              <div className="mb-5">
                <div className="w-11 h-11 bg-brand-50 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-key-2-line text-brand-500 text-xl" />
                </div>
                <h3 className="font-semibold text-gray-900">Generate API Key</h3>
                <p className="text-xs text-gray-400 mt-1">Link this key to one of your active subscriptions.</p>
              </div>

              {generateError && (
                <div className="mb-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                  <i className="ri-error-warning-fill text-red-500" />{generateError}
                </div>
              )}

              {activeApis.length > 0 ? (
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-1.5 block">Link to Subscription <span className="text-red-400">*</span></label>
                  <select
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white cursor-pointer"
                    value={selectedSubId}
                    onChange={e => { setSelectedSubId(e.target.value); setNewKeyName(activeApis.find(s => s.id === e.target.value)?.product_name ?? ''); }}
                  >
                    <option value="">— Select a subscription —</option>
                    {activeApis.map(s => (
                      <option key={s.id} value={s.id}>{s.product_name} ({s.pricing_type.replace(/_/g, '-')})</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1.5">The key name will match your selected product.</p>
                </div>
              ) : (
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-1.5 block">Key Name / Product</label>
                  <input
                    type="text"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="e.g. Payment Gateway API"
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    autoFocus
                  />
                  <p className="text-[11px] text-amber-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line" />No active subscriptions — key won&apos;t be tied to a plan.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setCreateModal(false); setNewKeyName(''); setSelectedSubId(''); setGenerateError(null); }}
                  className="flex-1 py-2.5 border border-surface-3 rounded-lg text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || (activeApis.length > 0 ? !selectedSubId : !newKeyName.trim())}
                  className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
                >
                  {creating ? <><i className="ri-loader-4-line animate-spin" />Generating...</> : <><i className="ri-key-2-line" />Generate Key</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Revoke Confirm Modal */}
      <AnimatePresence>
        {revokeConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-sm p-6">
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-forbid-line text-red-500 text-xl" />
                </div>
                <h3 className="font-semibold text-gray-900">Revoke API Key?</h3>
                <p className="text-xs text-gray-500 mt-1">This is permanent. Any requests using this key will immediately fail.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setRevokeConfirm(null)} className="flex-1 py-2.5 border border-surface-3 rounded-lg text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2">Cancel</button>
                <button
                  onClick={() => handleRevoke(revokeConfirm)}
                  disabled={!!actionLoading}
                  className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  Revoke Key
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Rotate Confirm Modal */}
      <AnimatePresence>
        {rotateConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-sm p-6">
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-refresh-line text-amber-500 text-xl" />
                </div>
                <h3 className="font-semibold text-gray-900">Rotate API Key?</h3>
                <p className="text-xs text-gray-500 mt-1">A new key will be generated. The old one stops working immediately. Update all integrations.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setRotateConfirm(null)} className="flex-1 py-2.5 border border-surface-3 rounded-lg text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2">Cancel</button>
                <button
                  onClick={() => handleRotate(rotateConfirm, keys.find(k => k.id === rotateConfirm)?.product_name ?? 'API MART')}
                  disabled={!!actionLoading}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  Rotate Key
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
